import React, { useState } from "react";
import { Upload, Button, message, Typography, Alert } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { RcFile } from "antd/es/upload";
import { useNotify } from "../../../../util-components/Notifications/notifications";
import { EditSiteConfiguration } from "../EditSiteConfiguration";
import { useSiteConfigurationValidation } from "./ValidateSiteConfiguration";
import { useQuery } from "@tanstack/react-query";
import { getSiteConfigurationSchemaOptions } from "../../../../../services/client/@tanstack/react-query.gen";
import {
  SiteConfigurationInput,
  SiteConfigurationOutputReadable,
} from "../../../../../services/client";
import { useAuthenticatedUser } from "../../../../Auth/AuthContext";
import { applyPxcVoltageFix } from "../../utils/pxcVoltageFix";
import { downloadJson } from "../../../../../util/downloadJson";
import { DisableForViewer } from "../../../../common/DisableForViewer";

const { Title, Paragraph } = Typography;

type UploadSiteConfigurationProps = {
  onChange: (data: SiteConfigurationInput) => void;
  initialData?: SiteConfigurationInput | SiteConfigurationOutputReadable | null;
  site_id: string;
};
export const UploadSiteConfiguration: React.FC<
  UploadSiteConfigurationProps
> = ({ onChange, initialData, site_id }) => {
  const [dirtyData, setDirtyData] = useState<object | undefined | null>(
    initialData,
  );

  const notify = useNotify();
  const user = useAuthenticatedUser();
  const { data: siteConfigurationSchema } = useQuery(
    getSiteConfigurationSchemaOptions(),
  );

  const { validate, validationErrors } = useSiteConfigurationValidation(
    siteConfigurationSchema || {},
  );

  const handleFileUpload = (file: RcFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (!e.target?.result || e.target.result === null) {
          notify({ message: "An empty file was uploaded.", type: "error" });
          return;
        }

        let json = JSON.parse(e.target.result as string);

        if (user?.partner_id === "pxc") {
          const { data: fixed, transformedCount } = applyPxcVoltageFix(json);
          if (transformedCount > 0) {
            json = fixed;
            notify({
              type: "warning",
              message: `Adapted ${transformedCount} collector${
                transformedCount === 1 ? "" : "s"
              }: phase_voltage set to 230`,
              description:
                "The line_voltage field is deprecated and will be removed in the near future. It was wrongly named and expects the phase_voltage instead of the line_voltage. The new field is phase_voltage. line_voltage=230 was thus replaced with phase_voltage=230.",
              duration: 0,
            });
          }
        }

        setDirtyData(json);

        if (validate(json)) {
          onChange(json);
        }

        message.success("File uploaded and parsed successfully!");
      } catch (_error) {
        message.error(
          "Failed to parse JSON file. Please check the file format.",
        );
      }
    };
    reader.readAsText(file);
    return false;
  };

  const handleFileDownload = () => {
    downloadJson(
      `${process.env.NODE_ENV}-site_configuration-${site_id}.json`,
      initialData,
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      <Title level={3}>Upload Site Configuration (JSON)</Title>
      <Paragraph type="secondary" className="w-full">
        Upload a JSON file containing the site configuration.
      </Paragraph>
      <DisableForViewer>
        <Upload
          beforeUpload={handleFileUpload}
          accept=".json"
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>Upload JSON</Button>
        </Upload>
      </DisableForViewer>
      {initialData && (
        <Button
          icon={<DownloadOutlined />}
          onClick={handleFileDownload}
          className="ml-2"
        >
          Download current JSON
        </Button>
      )}

      {validationErrors && validationErrors.length > 0 && (
        <Alert
          message="Validation Errors"
          description={
            <ul>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          className="mt-4"
        />
      )}
      {dirtyData && (
        <EditSiteConfiguration
          initialData={dirtyData}
          onChange={onChange}
          validate={validate}
        />
      )}
    </div>
  );
};

export default UploadSiteConfiguration;
